// Hydrate the member's Next Steps key material as soon as the app knows who
// they are (spec section 6.2, startup order): the courses store's annotation
// reads await `courses-keys/ready`, so a decrypt never races an empty store.
//
// WHO THEY ARE IS ASKED FOR HERE, NOT ASSUMED. Hydration is keyed on the
// profile id — the member id the API scopes every row and wrapping by — and
// nothing at the shell used to fetch the profile: it arrived as a side effect
// of whichever layout or route guard loaded it first. Every path into Next
// Steps runs through the pastoral guard, which does, so the gap never showed —
// until COURSES-E2EE-003 cold-loaded /courses/devices/recover with the guard's
// dev-only mock branch on (no profile fetch), reached the recover screen with
// no member id, and the typed code failed before any request: "memberId must
// be a positive integer" (2026-09-05). A token with no id is now a reason to
// fetch the profile, and the id it lands with brings the watcher back here.
//
// Keyed on the token as well as the profile id: on native the document
// survives a sign-out, so a member who signs back in as themselves keeps the
// same profile id and only the token changes; the logout wipe emptied the
// module, and this is what refills it. A different member id re-hydrates from
// scratch; a token refresh for a member already hydrated is a no-op inside the
// action.

/**
 * @param {unknown} id whatever the profile holds
 * @returns {number} the member id, or 0 while there is none
 */
function memberIdOf(id) {
  const memberId = Number(id);
  return Number.isInteger(memberId) && memberId > 0 ? memberId : 0;
}

/**
 * Never throws and never blocks: both dispatches are best-effort, and the
 * next change to who is signed in asks again.
 *
 * @param {{ store: object }} args
 * @returns {() => void} stops watching
 */
export function installKeyHydration({ store }) {
  if (!store) throw new TypeError('installKeyHydration needs the root Vuex store');

  // One derived value rather than a tuple: a watcher over an array rebuilds it
  // on every evaluation and so fires on changes that are not this one.
  const signal = state => {
    const token = state.user?.token;
    return token ? `${memberIdOf(state.profile?.id)}:${token}` : '';
  };

  const check = () => {
    const token = store.state.user?.token;
    if (!token) return;
    const memberId = memberIdOf(store.state.profile?.id);
    if (!memberId) {
      // Signed in, identity not yet known: ask. `fetchProfile` is a no-op once
      // the profile is loaded and joins a request already in the air, so on
      // the routes that fetch it anyway this costs nothing.
      store.dispatch('profile/fetchProfile').catch(error => {
        console.warn('[CoursesKeys] The profile the keys are hydrated for did not load:', error);
      });
      return;
    }
    store.dispatch('courses-keys/hydrate', { memberId }).catch(error => {
      console.warn('[CoursesKeys] Hydration failed:', error);
    });
  };

  // Watching BEFORE the first check, not after: a profile that answers within
  // the same tick would land the id before a watcher installed afterwards had
  // read its first value, and nothing would ever hydrate.
  return store.watch(signal, check, { immediate: true });
}
